/*
 * The Visits and Profiles filter state as a querystring.
 *
 * Only `%` and `,` are escaped inside a value, so a link stays readable in the address bar and one
 * written by hand works. Decoding never throws: a mangled link loses filters, not the page.
 */

import { Params } from '@angular/router';
import { DataPlatformExcludeFilter, DataPlatformSessionFilter, DataPlatformViewFilter } from './data-platform.model';
import { ENGAGEMENT_TIERS, tiersToScoreBands } from './data-platform.utils';
import { SOCIAL_SITES, parseReferrer } from './referrer-domain.utils';

export type DataPlatformFilterUrlFilter = Omit<DataPlatformViewFilter, 'page' | 'pageSize'>;

export interface DataPlatformFilterUrlState {
  filter: DataPlatformFilterUrlFilter;
  sortDirection?: 'asc' | 'desc';
  /** `YYYY-MM-DD`. Visits: the visit window. Profiles: the last-seen range. */
  from?: string;
  to?: string;
}

export interface FilterUrlOptions {
  /** The page's resting identity value, left out of the link. Visits rests on "all", Profiles on "identified". */
  identifiedDefault?: 'all' | 'identified';
  /** Profiles always; Visits only in a copied link, since the shell writes its window. */
  includeDates?: boolean;
}

/** The params the list page owns. On Visits the shell owns the date window and writes it separately. */
export const FILTER_URL_PARAMS = [
  'q',
  'goals',
  'xgoals',
  'tags',
  'xtags',
  'utm',
  'xutm',
  'dim',
  'xdim',
  'widgets',
  'we',
  'source',
  'app',
  'ref',
  'xref',
  'country',
  'device',
  'utmSource',
  'utmMedium',
  'dur',
  'login',
  'coupon',
  'purchase',
  'purchases',
  'boughtWithin',
  'amount',
  'nps',
  'csat',
  'visits',
  'tiers',
  'consent',
  'consentVersion',
  'identified',
  'via',
  'activity',
  'sort',
] as const;

export const DATE_URL_PARAMS = ['from', 'to'] as const;

export const VISITS_FILTER_URL_OPTIONS: FilterUrlOptions = { identifiedDefault: 'all' };

export const PROFILES_FILTER_URL_OPTIONS: FilterUrlOptions = { identifiedDefault: 'identified', includeDates: true };

const DEVICE_TYPES = ['desktop', 'mobile', 'tablet'] as const;
const LOGIN_STATUSES = ['logged_in', 'logged_out'] as const;
const COUPON_STATES = ['shown', 'copied'] as const;
const CONSENT_STATES = ['has_consent', 'has_not_consent'] as const;
const IDENTIFIED_STATES = ['identified', 'anonymous', 'all'] as const;
const ACTIVITY_STATES = ['seen', 'everyone', 'not-yet-seen'] as const;
const SORT_DIRECTIONS = ['asc', 'desc'] as const;
const TIER_KEYS = ENGAGEMENT_TIERS.map((tier) => tier.key);
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Only the params the state sets. Use this to build a link. */
export function encodeFilterUrlState(state: DataPlatformFilterUrlState, options: FilterUrlOptions = {}): Params {
  const filter = state.filter ?? {};
  const session = filter.session ?? {};
  const exclude = filter.exclude ?? {};
  const params: Params = {};
  const set = (key: string, value: string | undefined) => {
    if (value !== undefined) {
      params[key] = value;
    }
  };

  set('q', encodeText(filter.identifierFilter));
  set('goals', encodeList(filter.goals));
  set('xgoals', encodeList(exclude.goals));
  set('tags', encodeList(filter.tags));
  set('xtags', encodeList(exclude.tags));
  set('utm', encodeList(filter.utmCampaigns));
  set('xutm', encodeList(exclude.utmCampaigns));
  set('dim', encodeList(filter.dimensions));
  set('xdim', encodeList(exclude.dimensions));
  set('widgets', encodeNumberList(filter.widgetIds));
  set('we', encodeList(filter.widgetEventTypes));

  set('source', encodeList(session.sources));
  set('ref', encodeList(session.referrerDomains));
  set('xref', encodeList(exclude.referrerDomains));
  set('country', encodeList(session.countries));
  set('device', encodeList(session.deviceTypes));
  set('utmSource', encodeList(session.utmSources));
  set('utmMedium', encodeList(session.utmMediums));
  set('dur', encodeRange(session.minDurationSeconds, session.maxDurationSeconds));
  set('login', encodeText(session.loginStatus));
  set('coupon', encodeText(session.coupon));

  set('purchase', filter.hasPurchase === undefined ? undefined : filter.hasPurchase ? 'with' : 'without');
  set('purchases', encodeNumber(filter.minPurchaseCount));
  set('boughtWithin', encodeNumber(filter.lastPurchaseWithinDays));
  set('amount', encodeRange(filter.minPurchaseAmount, filter.maxPurchaseAmount));
  set('nps', encodeRange(filter.minNpsScore, filter.maxNpsScore));
  set('csat', encodeRange(filter.minCsatScore, filter.maxCsatScore));
  set('visits', encodeRange(filter.minVisits, filter.maxVisits));
  set('tiers', encodeList(filter.tiers));
  set('consent', encodeText(filter.consentFilter));
  set('consentVersion', encodeText(filter.consentVersionDate));
  set('identified', encodeIdentified(filter.identifiedStatus, options.identifiedDefault ?? 'all'));
  set('via', encodeList(filter.identitySources));
  set('activity', encodeActivity(filter));

  set('sort', state.sortDirection === 'asc' ? 'asc' : undefined);
  if (options.includeDates) {
    set('from', encodeDate(state.from));
    set('to', encodeDate(state.to));
  }

  return params;
}

/**
 * The same params plus `null` for the rest, so a merging `Router.navigate` drops what a previous
 * filter left behind. Keys outside this module, the shell's date window included, are untouched.
 */
export function filterUrlNavigationParams(state: DataPlatformFilterUrlState, options: FilterUrlOptions = {}): Params {
  const cleared: Params = {};
  const owned = options.includeDates ? [...FILTER_URL_PARAMS, ...DATE_URL_PARAMS] : FILTER_URL_PARAMS;
  for (const key of owned) {
    cleared[key] = null;
  }
  return { ...cleared, ...encodeFilterUrlState(state, options) };
}

export function decodeFilterUrlState(params: Params, options: FilterUrlOptions = {}): DataPlatformFilterUrlState {
  const read = (key: string): string | undefined => {
    const raw = params?.[key];
    if (Array.isArray(raw)) {
      return typeof raw[0] === 'string' ? raw[0] : undefined;
    }
    return typeof raw === 'string' ? raw : undefined;
  };

  const duration = decodeRange(read('dur'));
  const nps = decodeRange(read('nps'));
  const csat = decodeRange(read('csat'));
  const visits = decodeRange(read('visits'));
  const activity = decodeEnum(read('activity'), ACTIVITY_STATES);
  const tiers = decodeEnumList(read('tiers'), TIER_KEYS);
  const purchase = decodeEnum(read('purchase'), ['with', 'without'] as const);
  const amount = decodePurchaseAmount(read('amount'), purchase);

  const session: DataPlatformSessionFilter = {
    sources: decodeList(read('source')),
    referrerDomains: decodeReferrers(read('ref'), read('app')),
    countries: decodeList(read('country')),
    deviceTypes: decodeEnumList(read('device'), DEVICE_TYPES),
    utmSources: decodeList(read('utmSource')),
    utmMediums: decodeList(read('utmMedium')),
    minDurationSeconds: duration.min,
    maxDurationSeconds: duration.max,
    loginStatus: decodeEnum(read('login'), LOGIN_STATUSES),
    coupon: decodeEnum(read('coupon'), COUPON_STATES),
  };

  const goals = decodeList(read('goals'));
  const tags = decodeList(read('tags'));
  const utmCampaigns = decodeList(read('utm'));
  const dimensions = decodeList(read('dim'));
  const exclude: DataPlatformExcludeFilter = {
    goals: withoutIncluded(decodeList(read('xgoals')), goals),
    tags: withoutIncluded(decodeList(read('xtags')), tags),
    utmCampaigns: withoutIncluded(decodeList(read('xutm')), utmCampaigns),
    dimensions: withoutIncluded(decodeList(read('xdim')), dimensions),
    referrerDomains: withoutIncluded(decodeReferrers(read('xref'), undefined), session.referrerDomains),
  };

  const filter: DataPlatformFilterUrlFilter = {
    identifierFilter: decodeText(read('q')),
    goals,
    tags,
    utmCampaigns,
    dimensions,
    widgetIds: decodeNumberList(read('widgets')),
    widgetEventTypes: decodeList(read('we')),
    session: hasAnyValue(session) ? stripUndefined(session) : undefined,
    exclude: hasAnyValue(exclude) ? stripUndefined(exclude) : undefined,
    hasPurchase: purchase === undefined ? undefined : purchase === 'with',
    minPurchaseCount: decodeCount(read('purchases')),
    lastPurchaseWithinDays: decodeCount(read('boughtWithin')),
    minPurchaseAmount: amount.min,
    maxPurchaseAmount: amount.max,
    minNpsScore: nps.min,
    maxNpsScore: nps.max,
    minCsatScore: csat.min,
    maxCsatScore: csat.max,
    minVisits: visits.min,
    maxVisits: visits.max,
    tiers,
    engagementScoreBands: tiers?.length ? tiersToScoreBands(tiers) : undefined,
    consentFilter: decodeEnum(read('consent'), CONSENT_STATES),
    consentVersionDate: decodeText(read('consentVersion')),
    identifiedStatus: decodeIdentified(read('identified'), options.identifiedDefault ?? 'all'),
    identitySources: decodeList(read('via')),
    includeInactive: activity === 'everyone' ? true : undefined,
    notYetSeen: activity === 'not-yet-seen' ? true : undefined,
  };

  return stripUndefined({
    filter: stripUndefined(filter),
    sortDirection: decodeEnum(read('sort'), SORT_DIRECTIONS),
    from: options.includeDates ? decodeDate(read('from')) : undefined,
    to: options.includeDates ? decodeDate(read('to')) : undefined,
  });
}

/** `YYYY-MM-DD` in local time. `toISOString` would move the day across the timezone. */
export function toDateParam(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local start or end of the day, so a link covers the whole days it names. */
export function fromDateParam(value: string, edge: 'start' | 'end'): Date {
  return new Date(`${value}T${edge === 'start' ? '00:00:00' : '23:59:59'}`);
}

/** Whether anything is filtered, so a page can hide the link action when there is nothing to link to. */
export function hasFilterUrlState(filter: DataPlatformFilterUrlFilter, options: FilterUrlOptions = {}): boolean {
  return Object.keys(encodeFilterUrlState({ filter }, options)).length > 0;
}

/** "all" is the absence of an identity filter, so it is written only where the page rests elsewhere. */
function encodeIdentified(value: string | undefined, resting: 'all' | 'identified'): string | undefined {
  const current = value ?? 'all';
  return current === resting ? undefined : current;
}

function decodeIdentified(raw: string | undefined, resting: 'all' | 'identified'): string | undefined {
  const value = decodeEnum(raw, IDENTIFIED_STATES) ?? resting;
  return value === 'all' ? undefined : value;
}

function encodeActivity(filter: DataPlatformFilterUrlFilter): string | undefined {
  if (filter.notYetSeen) {
    return 'not-yet-seen';
  }
  return filter.includeInactive ? 'everyone' : undefined;
}

function encodeText(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? escapeValue(trimmed) : undefined;
}

function decodeText(raw: string | undefined): string | undefined {
  const value = raw === undefined ? undefined : unescapeValue(raw).trim();
  return value || undefined;
}

function encodeList(values: readonly string[] | undefined): string | undefined {
  const cleaned = (values ?? []).map((value) => value?.trim()).filter((value): value is string => !!value);
  return cleaned.length ? cleaned.map(escapeValue).join(',') : undefined;
}

function decodeList(raw: string | undefined): string[] | undefined {
  if (raw === undefined) {
    return undefined;
  }
  const values = raw
    .split(',')
    .map((part) => unescapeValue(part).trim())
    .filter((part) => part.length > 0);
  return values.length ? [...new Set(values)] : undefined;
}

/** `app` holds referrer apps, read as their platform; values the API rejects are dropped. */
function decodeReferrers(ref: string | undefined, app: string | undefined): string[] | undefined {
  const values = [
    ...(decodeList(ref) ?? []).map(parseReferrer),
    ...(decodeList(app) ?? []).map((value) => SOCIAL_SITES.find((site) => site.key === value)?.key),
  ].filter((value): value is string => !!value);
  return values.length ? [...new Set(values)] : undefined;
}

/** The API rejects a value both included and excluded, so a link naming it twice includes it. */
function withoutIncluded(
  excluded: string[] | undefined,
  included: readonly string[] | undefined,
): string[] | undefined {
  const values = (excluded ?? []).filter((value) => !included?.includes(value));
  return values.length ? values : undefined;
}

function encodeNumberList(values: readonly number[] | undefined): string | undefined {
  const cleaned = (values ?? []).filter((value) => Number.isFinite(value));
  return cleaned.length ? cleaned.join(',') : undefined;
}

function decodeNumberList(raw: string | undefined): number[] | undefined {
  const values = (decodeList(raw) ?? []).map(Number).filter((value) => Number.isFinite(value) && value > 0);
  return values.length ? [...new Set(values)] : undefined;
}

function encodeNumber(value: number | undefined): string | undefined {
  return value != null && Number.isFinite(value) ? String(value) : undefined;
}

/** `30-` from 30, `-120` up to 120, `0-0` bounces, `9-10` between. */
function encodeRange(min: number | undefined, max: number | undefined): string | undefined {
  const hasMin = min != null && Number.isFinite(min);
  const hasMax = max != null && Number.isFinite(max);
  if (!hasMin && !hasMax) {
    return undefined;
  }
  return `${hasMin ? min : ''}-${hasMax ? max : ''}`;
}

function decodeRange(raw: string | undefined): { min?: number; max?: number } {
  if (raw === undefined) {
    return {};
  }
  const separator = raw.indexOf('-');
  if (separator < 0) {
    return {};
  }
  return {
    min: decodeBound(raw.slice(0, separator)),
    max: decodeBound(raw.slice(separator + 1)),
  };
}

/** Bounds are seconds, visits, score points or amounts, so a negative is malformed. */
function decodeBound(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
}

/** The API accepts an amount only with a purchase and in order. */
function decodePurchaseAmount(
  raw: string | undefined,
  purchase: 'with' | 'without' | undefined,
): { min?: number; max?: number } {
  const amount = decodeRange(raw);
  if (purchase !== 'with' || (amount.min !== undefined && amount.max !== undefined && amount.max < amount.min)) {
    return {};
  }
  return amount;
}

function decodeCount(raw: string | undefined): number | undefined {
  const value = decodeBound(raw ?? '');
  return value !== undefined && value > 0 ? value : undefined;
}

function encodeDate(value: string | undefined): string | undefined {
  return value && DATE_PATTERN.test(value) ? value : undefined;
}

function decodeDate(raw: string | undefined): string | undefined {
  return raw && DATE_PATTERN.test(raw) && !Number.isNaN(Date.parse(raw)) ? raw : undefined;
}

function decodeEnum<T extends string>(raw: string | undefined, allowed: readonly T[]): T | undefined {
  const value = raw?.trim();
  return allowed.find((candidate) => candidate === value);
}

function decodeEnumList<T extends string>(raw: string | undefined, allowed: readonly T[]): T[] | undefined {
  const values = (decodeList(raw) ?? []).filter((value): value is T =>
    allowed.some((candidate) => candidate === value),
  );
  return values.length ? values : undefined;
}

/**
 * A value may contain the list separator, so escape it and the escape character itself. Angular
 * handles the rest, which keeps spaces and a dimension's `type:value` readable in the address bar.
 */
function escapeValue(value: string): string {
  return value.replace(/%/g, '%25').replace(/,/g, '%2C');
}

function unescapeValue(value: string): string {
  return value.replace(/%2C/gi, ',').replace(/%25/g, '%');
}

function hasAnyValue(source: object): boolean {
  return Object.values(source).some((value) => value !== undefined);
}

function stripUndefined<T extends object>(source: T): T {
  return Object.fromEntries(Object.entries(source).filter(([, value]) => value !== undefined)) as T;
}
